import type { Component, ReploComponentType } from "schemas/component";
import type { ReploElement } from "schemas/generated/element";

import {
  findAncestorComponentIndexPath,
  findComponent,
} from "replo-runtime/shared/utils/component";

function findComponentById(
  element: { component: Component } | null,
  testId: string | null,
): Component | null {
  if (!element) {
    return null;
  }

  return findComponent(element, (component) => component.id === testId);
}

function findParent(
  element: ReploElement | null,
  targetComponentId: string | null,
): Component | null {
  if (!targetComponentId || !element) {
    return null;
  }
  return findComponent(element, (possibleParent) => {
    return (possibleParent.children || [])
      .map((child) => child.id)
      .includes(targetComponentId);
  });
}

function findAncestorComponentOrSelf(
  element: ReploElement | null,
  targetComponentId: string | null,
  test: (component: Component, parent: Component | null) => boolean,
): Component | null {
  const targetComponent = findComponentById(element, targetComponentId);
  if (!targetComponent || !element) {
    return null;
  }
  if (test(targetComponent, findParent(element, targetComponentId))) {
    return targetComponent;
  }
  const { component } = findAncestorComponentIndexPath(
    element,
    targetComponent.id,
    test,
  );
  return component;
}

export function getCanBeAddedFunction(
  ancestorType: ReploComponentType,
  rejectionMessage: string,
) {
  return (extras: {
    parent: Component | null;
    element: ReploElement;
  }): { canBeAdded: true } | { canBeAdded: false; message: string } => {
    const matchingAncestor = findAncestorComponentOrSelf(
      extras.element,
      extras.parent?.id ?? null,
      (c) => c.type === ancestorType,
    );
    if (!matchingAncestor) {
      return {
        canBeAdded: false,
        message: rejectionMessage,
      };
    }
    return { canBeAdded: true };
  };
}
